import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys, useAuthenticatedClient } from '@/api/queries'
import type { components } from '@/api/schema'
import { invoiceKeys } from '@/features/invoices/keys'

export type ActionInvoice = components['schemas']['ActionInvoiceRead']
export type ActionPaymentFields = components['schemas']['ActionPaymentFields']
export type ActionPaymentCreate = components['schemas']['ActionPaymentCreate']
export type ActionPaymentDraft = components['schemas']['ActionPaymentDraftRead']
export type ActionPaymentRecorded = components['schemas']['ActionPaymentRecordedRead']

/** The confirmation a pay-in-advance supplier would get for this payment, before anything is recorded (USA-103). */
export function useActionPaymentDraft(invoiceUuid: string) {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: ActionPaymentFields) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/ap-invoices/{uuid}/action-payments/draft', {
        params: { path: { uuid: invoiceUuid } },
        body,
      })
      if (error) throw error
      return data
    },
  })
}

/** Record the payment from its action item: the invoice is paid, the item closes and the supplier is emailed. */
export function useRecordActionPayment(invoiceUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ActionPaymentCreate) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/ap-invoices/{uuid}/action-payments', {
        params: { path: { uuid: invoiceUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardSummary.all })
    },
  })
}
